import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import JsBarcode from "jsbarcode";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, ArrowLeft, Sticker, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Sticker presets — all sizes in mm. `stickersPerRow` controls how many
// stickers land side-by-side on the same physical page (thermal roll strip).
const SIZES = {
  tag_3up: { label: "Jewelry Tag — 3 per row (100 × 20 mm strip)", pageW: 100, pageH: 20, w: 33.33, h: 20, barcodeH: 24, stickersPerRow: 3 },
  bat:     { label: "Bat Sticker (52 × 20 mm)",                    pageW: 52,  pageH: 20, w: 52,    h: 20, barcodeH: 24, stickersPerRow: 1 },
  rect:    { label: "Rectangle (40 × 25 mm)",                      pageW: 40,  pageH: 25, w: 40,    h: 25, barcodeH: 30, stickersPerRow: 1 },
  square:  { label: "Square (30 × 30 mm)",                         pageW: 30,  pageH: 30, w: 30,    h: 30, barcodeH: 24, stickersPerRow: 1 },
};

// Parse a bulk items payload from URL — sessionStorage key. Falls back to
// single-item mode using ?product_id=&qty=.
function readBulkFromSession(key) {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length ? arr : null;
  } catch { return null; }
}

export default function StickerPrint() {
  const [params] = useSearchParams();
  const initialSize = SIZES[params.get("size")] ? params.get("size") : "tag_3up";
  const bulkKey = params.get("bulk");
  const bulk = useMemo(() => readBulkFromSession(bulkKey), [bulkKey]);

  const [items, setItems] = useState([]);   // [{product_id, qty, product, ref_type, ref_id, ref_no}]
  const [size, setSize] = useState(initialSize);
  const [loading, setLoading] = useState(true);
  const [logged, setLogged] = useState(false);

  useEffect(() => {
    (async () => {
      const src = bulk || [{
        product_id: params.get("product_id"),
        qty: Math.max(1, Math.min(10000, parseInt(params.get("qty") || "1", 10) || 1)),
        ref_type: params.get("ref_type") || "manual",
        ref_id: params.get("ref_id") || null,
        ref_no: params.get("ref_no") || null,
      }].filter((x) => x.product_id);
      const resolved = [];
      for (const it of src) {
        try {
          const r = await api.get(`/products/${it.product_id}`);
          resolved.push({ ...it, product: r.data });
        } catch { /* skip missing */ }
      }
      setItems(resolved);
      setLoading(false);
    })();
  }, [bulk, params]);

  // Flatten items → per-piece array for rendering, applying qty
  const pieces = useMemo(() => {
    const out = [];
    items.forEach((it, groupIdx) => {
      const q = Math.max(0, Math.min(10000, Number(it.qty) || 0));
      for (let i = 0; i < q; i++) out.push({ ...it, _groupIdx: groupIdx, _pieceIdx: i });
    });
    return out;
  }, [items]);

  const stickerRefs = useRef([]);

  // Render barcode on every sticker whenever data/size changes
  useEffect(() => {
    if (!pieces.length) return;
    const preset = SIZES[size];
    // Wait a tick for canvases to mount
    const t = setTimeout(() => {
      stickerRefs.current.forEach((canvas, idx) => {
        if (!canvas || !pieces[idx]) return;
        const p = pieces[idx].product;
        const value = p?.sku || p?.code || p?.id || "N/A";
        try {
          JsBarcode(canvas, String(value), {
            format: "CODE128",
            displayValue: false,
            margin: 0,
            width: 1.2,
            height: preset.barcodeH,
            background: "#ffffff",
            lineColor: "#000000",
          });
        } catch { /* ignore malformed */ }
      });
    }, 30);
    return () => clearTimeout(t);
  }, [pieces, size]);

  const updateItem = (idx, patch) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const logPrint = useCallback(async () => {
    for (const it of items) {
      const q = Number(it.qty) || 0;
      if (q <= 0) continue;
      try {
        await api.post("/production/sticker-prints", {
          product_id: it.product_id, qty: q,
          ref_type: it.ref_type, ref_id: it.ref_id, ref_no: it.ref_no,
          sticker_size: size,
        });
      } catch { /* soft-fail; audit only */ }
    }
    setLogged(true);
  }, [items, size]);

  const handlePrint = async () => {
    if (!pieces.length) return toast.error("Nothing to print");
    if (!logged) await logPrint();
    setTimeout(() => window.print(), 250);
  };

  if (loading) return <div className="p-10 text-center text-gray-400">Loading…</div>;
  if (!items.length) return (
    <div className="p-10 text-center text-red-500">
      No printable items. <a href="/production/quality-control" className="underline text-blue-600">Go back</a>
    </div>
  );

  const preset = SIZES[size];
  const totalPieces = pieces.length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toolbar */}
      <div className="no-print bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center gap-3 p-3 flex-wrap">
          <a href="#" onClick={(e) => { e.preventDefault(); window.close(); if (!window.opener) window.history.back(); }} className="text-gray-500 hover:text-gray-900 flex items-center gap-1 text-sm" data-testid="sticker-back">
            <ArrowLeft className="h-4 w-4" /> Close
          </a>
          <div className="flex items-center gap-2 ml-2">
            <Sticker className="h-5 w-5 text-[#9B1C1C]" />
            <h1 className="font-heading font-bold text-lg">Sticker Print</h1>
          </div>
          <span className="text-xs text-gray-500">{items.length} product(s) · <span className="font-semibold text-gray-800">{totalPieces}</span> sticker(s) total</span>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <label className="text-xs text-gray-600 flex items-center gap-2">
              Size
              <select value={size} onChange={(e) => setSize(e.target.value)} className="h-8 rounded-md border border-gray-200 text-xs px-2" data-testid="sticker-size">
                {Object.entries(SIZES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
            {logged && <span className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Print logged</span>}
            <Button onClick={handlePrint} className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" disabled={totalPieces === 0} data-testid="sticker-print-btn">
              <Printer className="h-4 w-4" /> Print {totalPieces} sticker{totalPieces === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
        {/* Editable qty table */}
        <div className="max-w-6xl mx-auto px-3 pb-3">
          <table className="w-full text-xs bg-white border border-gray-200 rounded-md overflow-hidden">
            <thead className="bg-gray-50">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-2 py-1.5">Product</th>
                <th className="px-2 py-1.5">SKU</th>
                <th className="px-2 py-1.5">Ref</th>
                <th className="px-2 py-1.5 text-right w-24">Qty</th>
                <th className="px-2 py-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="px-2 py-1.5 font-medium">{it.product?.name || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-gray-600">{it.product?.sku || it.product?.code || "—"}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-gray-500">{it.ref_no || "—"}</td>
                  <td className="px-2 py-1.5 text-right">
                    <Input type="number" min="0" max="10000" value={it.qty} onChange={(e) => updateItem(i, { qty: Math.max(0, Math.min(10000, parseInt(e.target.value || "0", 10) || 0)) })}
                      className="h-7 w-20 text-right tabular-nums font-mono ml-auto text-xs"
                      data-testid={`sticker-qty-${i}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    {items.length > 1 && (
                      <button onClick={() => removeItem(i)} className="h-6 w-6 rounded hover:bg-red-50 inline-flex items-center justify-center" data-testid={`sticker-remove-${i}`}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-gray-500 mt-1.5">
            Ensure your <b>thermal printer</b> is set as the default in the browser print dialog and paper matches the sticker template.
            Barcode is CODE128 of the product SKU. Adjust qty inline before printing.
          </p>
        </div>
      </div>

      {/* Sticker sheet */}
      <div className="p-4 print-sheet">
        <div
          className="mx-auto"
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${preset.stickersPerRow}, ${preset.w}mm)`,
            gap: 0,
            width: `${preset.pageW}mm`,
            justifyContent: "start",
          }}
        >
          {pieces.map((pc, i) => {
            const p = pc.product;
            const mrp = p?.mrp || p?.sale_price || p?.retail_price;
            return (
              <div
                key={i}
                className="sticker-card bg-white border border-gray-300 flex flex-col items-center justify-between text-center overflow-hidden"
                style={{ width: `${preset.w}mm`, height: `${preset.h}mm`, padding: "0.6mm 1mm", boxSizing: "border-box" }}
                data-testid={`sticker-card-${i}`}
              >
                <div className="w-full text-[6pt] leading-none font-semibold truncate" style={{ fontFamily: "system-ui, sans-serif" }}>
                  {p?.name}
                </div>
                <canvas ref={(el) => (stickerRefs.current[i] = el)} style={{ maxWidth: "100%", maxHeight: `${preset.barcodeH}px` }} />
                <div className="w-full flex items-center justify-between text-[5.5pt] leading-none font-mono gap-1" style={{ fontFamily: "monospace" }}>
                  <span className="truncate">{p?.sku || p?.code}</span>
                  {mrp != null && <span className="font-bold text-[6.5pt]">₹{Number(mrp).toLocaleString("en-IN")}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Print CSS — page size matches the strip; page break after every row of stickers */}
      <style>{`
        @media screen {
          .sticker-card { box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
        }
        @media print {
          @page {
            size: ${preset.pageW}mm ${preset.pageH}mm;
            margin: 0;
          }
          html, body { background: #fff !important; margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .print-sheet { padding: 0 !important; }
          .print-sheet > div { gap: 0 !important; margin: 0 !important; }
          .sticker-card {
            border: none !important;
            width: ${preset.w}mm !important;
            height: ${preset.h}mm !important;
            break-inside: avoid;
          }
          /* Force page break after every N stickers where N = stickersPerRow */
          .sticker-card:nth-child(${preset.stickersPerRow}n) {
            page-break-after: always;
            break-after: page;
          }
          .sticker-card:last-child {
            page-break-after: auto;
            break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}
